import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from './config.model';
import { SessionWebWorkerService } from '../services/session-web-worker.service';
import { HeapAnalyticsService } from '../services/heap-analytics.service';
import { AtomSessionService } from '../services/atom-session.service';

/** Path prefix of the Fund Connect service, served on the ATOM host (see FundConnectApiService). */
const FUND_CONNECT_PATH_SEGMENT: string = '/fundconnect/';

class Logout {
	static readonly type: string = '[Auth] Logout';
}

class SetErrorMessage {
	static readonly type: string = '[Auth] SetErrorMessage';
	constructor(public errorMessage: string) {}
}

@Injectable({
	providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
	private accessToken: string;

	constructor(
		@Inject(ENVIRONMENT_TOKEN) private environment: DefaultEnvironment,
		private store: Store,
		private sessionService: SessionWebWorkerService,
		private heapService: HeapAnalyticsService,
		private injector: Injector
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (localStorage.auth) {
			this.accessToken = localStorage.auth;
		}

		const secureReq: HttpRequest<any> = req.clone({
			url: req.url.startsWith('http://localhost:') ? req.url : req.url.replace('http://', 'https://'),
		});

		const allowWithoutAuth: boolean = this.allowWithoutAuth(secureReq.url);

		if (allowWithoutAuth) {
			return next.handle(secureReq.clone());
		}
		// Cross-origin ATOM requests need withCredentials:true so the browser
		// sends/receives the ALB's AWSALB stickiness cookie. Without it the
		// formId-bound cache lookup hits a different node from bootstrap and
		// returns 410 FORM_EXPIRED. Scoped to atomApiUrl-matched URLs so
		// other API integrations (Trovata API, edge, etc.) are unaffected.
		// The Fund Connect service shares that host but authenticates by bearer only and answers CORS with
		// allow-credentials=false: a credentialed request to it is blocked by the browser, so leave it out.
		const isAtomCrossOrigin: boolean =
			!!this.environment.atomApiUrl && secureReq.url.startsWith(this.environment.atomApiUrl) && !secureReq.url.includes(FUND_CONNECT_PATH_SEGMENT);

		if (this.accessToken && this.isApi(secureReq.url)) {
			if (this.isAtomUrl(secureReq.url)) {
				// Resolved lazily: AtomSessionService posts through HttpClient, whose chain holds
				// this interceptor, so it cannot be a constructor dependency.
				this.injector.get(AtomSessionService).touch();
			}
			if (this.sessionService.isAccessTokenValid()) {
				const clonedReq: HttpRequest<any> = secureReq.clone({
					headers: secureReq.headers.set('Authorization', `Bearer ${this.accessToken}`),
					...(isAtomCrossOrigin ? { withCredentials: true } : {}),
				});
				return this.handleClonedRequest(clonedReq, next);
			} else {
				// not catch needed, log outs of app on failure to refresh
				return from(this.getNewAccessToken()).pipe(
					switchMap((newAccessToken: string) => {
						const clonedReq: HttpRequest<any> = secureReq.clone({
							headers: secureReq.headers.set('Authorization', `Bearer ${newAccessToken}`),
							...(isAtomCrossOrigin ? { withCredentials: true } : {}),
						});
						return this.handleClonedRequest(clonedReq, next);
					})
				);
			}
		} else {
			return next.handle(req);
		}
	}

	private handleClonedRequest(clonedReq: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(clonedReq).pipe(
			tap(
				() => {},
				async (httpError: HttpErrorResponse) => {
					this.heapService.apiError(JSON.stringify(httpError));

					const route: string = document.location.href.split(document.location.origin)[1];

					if (route === '/login' && clonedReq.url === `${this.environment.edgeAPI()}/user` && httpError.error && httpError.error.message) {
						const errorMessage: string = httpError.error.message;
						this.store.dispatch(new SetErrorMessage(errorMessage));
					}

					if (
						((httpError && httpError.status && httpError.status === 401) ||
							(httpError && httpError.error && httpError.error.status && httpError.error.status === 401)) &&
						// TODO remove this when change password failure is not 401: https://trovata.atlassian.net/browse/CP-274
						!(httpError && httpError.url?.includes('/auth/password')) &&
						// TODO remove/revisit when ATOM backend uses Bearer auth: menu 401 should not log the user out
						!(httpError && httpError.url?.startsWith(this.environment.trovataAPI('menu'))) &&
						// The Fund Connect service shares atomApiUrl's host; its own auth failures must not end the session
						!(httpError && httpError.url?.includes(FUND_CONNECT_PATH_SEGMENT))
					) {
						this.store.dispatch(new Logout());
					}

					if (httpError && httpError.error && httpError.error.message) {
						return throwError(httpError.error.message);
					} else if (httpError && httpError.message) {
						return throwError(httpError.message);
					} else {
						return throwError('Something bad happened; please try again later.');
					}
				}
			)
		);
	}

	private getNewAccessToken(): Promise<string> {
		return this.sessionService.refreshTokens();
	}

	/** ATOM REST: the configured ATOM origin, or relative /api/ when local dev proxies it (atomApiUrl ''). */
	private isAtomUrl(url: string): boolean {
		return this.environment.atomApiUrl ? url.startsWith(this.environment.atomApiUrl) : url.startsWith('/api/');
	}

	private isApi(url: string): boolean {
		if (
			url.includes(this.environment.edgeApi) ||
			url.includes(this.environment.trovataApi) ||
			url.includes(this.environment.developerApi) ||
			// atomApiUrl defaults to '' (DEFAULT_ENVIRONMENT_CONFIG); only env.dev.ts
			// overrides it. Without the truthy guard, `url.includes('')` is true for
			// every URL — leaking the Bearer token to any third-party domain hit via
			// HttpClient. Mirror the guard already used on isAtomCrossOrigin (line 52).
			(!!this.environment.atomApiUrl && url.includes(this.environment.atomApiUrl)) ||
			url.includes('https://api.trovata.io/manage') ||
			url.includes('https://api.trovata.io/payments-sandbox') ||
			url.includes('http://localhost:') ||
			// Local-dev proxy path: environment.local.ts sets atomApiUrl='' and
			// proxy.conf.js forwards /api/* to ATOM. Requests reach the interceptor
			// as relative '/api/...' URLs, which none of the branches above catch.
			// In dev/prod atomApiUrl is set so URLs are absolute — this is a no-op.
			url.startsWith('/api/')
		) {
			return true;
		} else {
			return false;
		}
	}

	private allowWithoutAuth(url: string): boolean {
		if (
			url.includes('/auth/token') ||
			url.includes('/login/netsuite') ||
			url.includes('/login/forgotPassword') ||
			url.includes('/connections/customer') ||
			url.includes('/connections/recaptcha/sitekey') ||
			url.includes('/connections/recaptcha') ||
			url.includes('/auth/token/verification') ||
			url.includes('/auth/token/verification/request')
		) {
			return true;
		} else {
			return false;
		}
	}
}
